<template>
  <div class="form-floating">
    <input
      id="email"
      type="email"
      maxlength="128"
      v-model="formEmail"
      class="form-control pj-input"
      :class="[
        { 'is-invalid': errorMessage },
        { 'fs-3 pj-ss-input': isSelfService },
      ]"
      @keyup="updateInput"
      @change="emailChanged"
      @input="$emit('input', $event.target.value)"
      aria-describedby="emailHelp"
      :placeholder="labelName"
      :disabled="disabled"
    />
    <label
      for="email"
      class="form-label"
      :class="{ 'full fs-4': isSelfService }"
    >
      {{ labelName }}
    </label>
    <div
      v-if="errorMessage"
      class="invalid-feedback"
      :class="{ 'fs-3': isSelfService }"
    >
      {{ errorMessage }}
    </div>
    <div id="emailHelp" class="form-text"></div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watchEffect,
  WritableComputedRef,
} from "vue";
import { useI18n } from "vue-i18n";


export default defineComponent({
  name: "InputFieldEmail",
  emits: ["input-field-email-emit", "validate-emit", "input"],
  props: {
    labelName: String,
    placeholder: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    stateEmail: {
      type: String,
      default: null,
      required: false,
    },
    inputValue: {
      type: String,
      default: "",
    },
    required: { type: Boolean, default: true },
    isSelfService: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const formEmailvalue = ref(props.inputValue);
    const errorMessage = ref("");
    const error = ref(false);

    const formEmail: WritableComputedRef<string> = computed({
      get() {
        return formEmailvalue.value || props.stateEmail;
      },
      set(newValue) {
        formEmailvalue.value = newValue;
      },
    });

    const updateInput = (): void => {
      errorMessage.value = "";
      if (!formEmail.value) {
        emit("validate-emit", false);
      }
      emit("validate-emit", true);
    };

    const isEmailFormatValid = (email: string): boolean => {
      const regExp =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return regExp.test(email);
    };

    watchEffect(() => {
      if (props.inputValue !== undefined) {
        formEmailvalue.value = props.inputValue;
      }
    });

    const validate = (): boolean => {
      if (formEmail.value) {
        if (!isEmailFormatValid(formEmail.value)) {
          errorMessage.value = t("SHARED.EMAIL_INVALID_ERROR_TEXT");
          error.value = true;
          emit("validate-emit", false);
          return false;
        }
        emit("input-field-email-emit", formEmail.value);
        emit("validate-emit", true);
        return true;
      }

      if (props.required) {
        errorMessage.value = t("SHARED.EMAIL_ERROR_TEXT");
        error.value = true;
        emit("validate-emit", false);
        return false;
      }
      emit("input-field-email-emit", formEmail.value);
      emit("validate-emit", true);
      return true;
    };

    const getInputFieldValue = () => {
      return formEmail.value;
    };

    const emailChanged = () => {
      validate();
    };

    onMounted(() => {
      if (formEmail.value) validate();
    });

    return {
      t,
      formEmail,
      errorMessage,
      getInputFieldValue,
      updateInput,
      emailChanged,
      validate,
    };
  },
});
</script>
<style lang="scss" scoped>
.form-label {
  white-space: nowrap;
}

input {
  border-radius: 5px;
}
</style>
